import * as React from 'react';
import styled, { css } from 'styled-components';
import { body2 } from '@westwing/ui-kit/typography';
import { forMidWideScreens, forTablet } from '@westwing/ui-kit/breakPoints';
import ArrowIcon, { Direction } from '@westwing/ui-kit/Icons/ArrowIcon';
import { useLooksTracking } from '../hooks/useLooksTracking';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { TrackLooksActions } from 'Common/ga/types';
import { BellaTestIds } from 'Common/qaIds';
import { ensureEndingSlash } from 'Client/urlService';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';

export const POKey = {
    PREVIOUS_LINK: 'Vorheriger Look',
    NEXT_LINK: 'Nächster Look',
};

const StyledLink = styled.span`
    ${body2}
    color: ${({ theme }) => theme.colors.charcoal100};
    letter-spacing: 0.35px;
    cursor: pointer;
`;

const StyledConnectedLooks = styled.div<{ p100IncreasedDesktopPageWidth?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 48px;
    padding: 20px;

    ${forTablet`
        padding: 0;
    `}

    ${ArrowIcon}[direction="left"] {
        margin-right: 14px;
    }

    ${ArrowIcon}[direction="right"] {
        margin-left: 14px;
    }

    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            ${forMidWideScreens`
                height: 60px;
            `}
        `}
`;

const StyledConnectedLooksPlaceholder = styled.span<{ p100IncreasedDesktopPageWidth?: boolean }>`
    visibility: hidden;
    height: 48px;
    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            ${forMidWideScreens`
                height: 60px;
            `}
        `}
`;

const ConnectedLooks: React.FC<{ p100IncreasedDesktopPageWidth?: boolean }> = ({ p100IncreasedDesktopPageWidth }) => {
    const t = useBellaTranslator();
    const { trackingName, prevLook, nextLook } = useBellaSelector(state => state.lookDetailPage.look);
    const { LDP } = useLooksTracking();
    const dispatch = useBellaDispatch();
    const push = useBellaPush();

    if (!prevLook && !nextLook) {
        return (
            <StyledConnectedLooksPlaceholder
                data-testid={BellaTestIds.ldpPlaceholderLookLink}
                p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
            />
        );
    }

    return (
        <StyledConnectedLooks p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}>
            {prevLook ? (
                <StyledLink
                    data-href={ensureEndingSlash(`/looks/${prevLook.slug}`)}
                    tabIndex={0}
                    role="link"
                    data-testid={BellaTestIds.ldpPrevLookLink}
                    onClick={() => {
                        push(ensureEndingSlash(`/looks/${prevLook.slug}`), true, {}, true);
                        dispatch(setLDPKnownInfoAction(prevLook));
                        LDP.trackConnectedLooksClick(TrackLooksActions.LDPPrevLookClick, trackingName);
                    }}
                >
                    <ArrowIcon size={10} direction={Direction.Left} /> {t(POKey.PREVIOUS_LINK)}
                </StyledLink>
            ) : (
                <StyledConnectedLooksPlaceholder data-testid={BellaTestIds.ldpPlaceholderLookLink} />
            )}
            {nextLook ? (
                <StyledLink
                    data-href={ensureEndingSlash(`/looks/${nextLook.slug}`)}
                    tabIndex={0}
                    role="link"
                    data-testid={BellaTestIds.ldpNextLookLink}
                    onClick={() => {
                        push(ensureEndingSlash(`/looks/${nextLook.slug}`), true, {}, true);
                        dispatch(setLDPKnownInfoAction(nextLook));
                        LDP.trackConnectedLooksClick(TrackLooksActions.LDPNextLookClick, trackingName);
                    }}
                >
                    {t(POKey.NEXT_LINK)} <ArrowIcon size={10} direction={Direction.Right} />
                </StyledLink>
            ) : (
                <StyledConnectedLooksPlaceholder data-testid={BellaTestIds.ldpPlaceholderLookLink} />
            )}
        </StyledConnectedLooks>
    );
};

export { ConnectedLooks };
